import * as React from 'react';
import clsx from 'clsx';
import dechoesIcon from '@/assets/images/icons/group_chat_avatar.svg';
import { AvatarWaves } from '@/components/common/AvatarWaves';
import { ConfirmModal } from '@/components/chat/ChatHeader';
import type { LocalGroupChat } from '../types';

/** Props for the {@link GroupChatListItem} component. */
interface GroupChatListItemProps {
  /** Group chat data to render. */
  group: LocalGroupChat;
  /** Whether this group is currently selected. */
  isActive: boolean;
  /** Callback when the row is clicked. */
  onClick: () => void;
  /** Callback to delete this group, receives the group ID. */
  onDelete: (groupId: string) => void;
}

/** Row item for a locally-persisted group chat with dropdown menu and delete confirmation. */
export const GroupChatListItem: React.FC<GroupChatListItemProps> = React.memo(
  ({ group, isActive, onClick, onDelete }) => {
    const [isMenuOpen, setIsMenuOpen] = React.useState(false);
    const [isDeleteOpen, setIsDeleteOpen] = React.useState(false);
    const menuRef = React.useRef<HTMLDivElement>(null);

    React.useEffect(() => {
      if (!isMenuOpen) return;
      const handleClickOutside = (e: MouseEvent) => {
        if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
          setIsMenuOpen(false);
        }
      };
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [isMenuOpen]);

    return (
      <>
        <button
          type='button'
          onClick={onClick}
          className={clsx(
            'relative flex w-full items-center gap-4 rounded-24 px-0 py-4 text-left transition-all duration-200 xl:w-[389px] xl:px-[20px]',
            isActive
              ? 'bg-transparent xl:bg-brand-840 xl:shadow-lg'
              : 'bg-transparent hover:bg-gray-50'
          )}
        >
          <div className='relative h-[52px] w-[74px] shrink-0'>
            <div className='absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2'>
              <div className='relative'>
                <div
                  className='flex items-center justify-center rounded-full border-3 border-brand-300'
                  style={{
                    width: '51.37px',
                    height: '51.37px',
                    backgroundColor: '#392278',
                  }}
                >
                  <img src={dechoesIcon} alt='Group' />
                </div>
                <AvatarWaves side='right' />
              </div>
            </div>
          </div>

          <span
            className={clsx(
              'min-w-0 flex-1 whitespace-normal break-words font-sans text-[14px] font-bold uppercase leading-[18px] tracking-normal',
              isActive ? 'text-brand-750 xl:text-white' : 'text-brand-750'
            )}
          >
            {group.name}
          </span>

          <div ref={menuRef} className='relative mr-4 shrink-0'>
            <div
              role='button'
              tabIndex={0}
              onClick={(e) => {
                e.stopPropagation();
                setIsMenuOpen((prev) => !prev);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  e.stopPropagation();
                  setIsMenuOpen((prev) => !prev);
                }
              }}
              className='flex size-6 items-center justify-center rounded-full transition-colors hover:bg-black/5'
              aria-label='Group options'
            >
              <svg width='10' height='6' viewBox='0 0 10 6' fill='none'>
                <path
                  d='M1 1l4 4 4-4'
                  stroke='#1A1036'
                  strokeWidth='1.5'
                  strokeLinecap='round'
                  strokeLinejoin='round'
                />
              </svg>
            </div>

            {isMenuOpen && (
              <div className='absolute right-0 top-full z-30 mt-1 w-32 overflow-hidden rounded-lg bg-white shadow-lg'>
                <button
                  type='button'
                  className='flex w-full items-center gap-2 px-3 py-2.5 text-left font-sans text-[13px] font-medium text-red-500 transition-colors hover:bg-gray-50'
                  onClick={(e) => {
                    e.stopPropagation();
                    setIsMenuOpen(false);
                    setIsDeleteOpen(true);
                  }}
                >
                  <svg width='14' height='14' viewBox='0 0 14 14' fill='none'>
                    <path
                      d='M1.75 3.5h10.5M5.25 6.125v3.5M8.75 6.125v3.5M2.625 3.5l.583 7.583a1.167 1.167 0 001.167 1.042h5.25a1.167 1.167 0 001.167-1.042l.583-7.583M4.667 3.5V2.333A1.167 1.167 0 015.833 1.167h2.334A1.167 1.167 0 019.333 2.333V3.5'
                      stroke='currentColor'
                      strokeWidth='1.2'
                      strokeLinecap='round'
                      strokeLinejoin='round'
                    />
                  </svg>
                  Delete
                </button>
              </div>
            )}
          </div>
        </button>

        <ConfirmModal
          isOpen={isDeleteOpen}
          onClose={() => setIsDeleteOpen(false)}
          onConfirm={() => {
            onDelete(group.id);
            setIsDeleteOpen(false);
          }}
          title='Delete Group Chat?'
          message={
            <>
              Are you sure you want to delete <span className='font-bold'>{group.name}</span>?{' '}
              <span className='font-bold'>This action cannot be undone.</span>
            </>
          }
          confirmLabel='Delete'
        />
      </>
    );
  }
);

GroupChatListItem.displayName = 'GroupChatListItem';
